import React from "react";
import { makeStyles } from "@mui/styles";
import { MailProDialog } from "views/Pma/MailPro/components/MailProDialog/MailProDialog";
import { MailProDialogHeader } from "views/Pma/MailPro/components/MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "views/Pma/MailPro/components/MailProDialog/MailProDialogBody";
import MailProButton from "views/Pma/MailPro/components/UI/MailProButton";
import { getRangeDisplayFieldDefs, hasDisplayValue } from "./rangeDisplayUtils";

const DIALOG_BUTTON_BORDER_RADIUS = "8px";
const DIALOG_BUTTON_MIN_HEIGHT = "30px";
export const MAX_RANGE_FIELD_LENGTH = 25;

const useStyles = makeStyles({
  dialogPaper: {
    maxWidth: "920px",
    width: "90vw",
    "& .MuiDialogContent-root": { padding: "0 !important" },
  },
  dialogHeader: {
    backgroundColor: "#4A7396",
    color: "white",
    padding: "8px 24px",
    textAlign: "center",
    width: "100%",
  },
  headerTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
  },
  dialogBody: {
    padding: "20px 24px 12px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  instruction: {
    fontSize: "13px",
    lineHeight: 1.45,
    color: "#4b5563",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
  },
  fields: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px 24px",
    alignItems: "start",
  },
  fieldColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    minWidth: 0,
  },
  fieldRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
  },
  visibility: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flex: "0 0 auto",
    paddingTop: "28px",
    fontFamily: "objektiv-mk2, sans-serif",
    fontSize: "12px",
    fontWeight: 600,
    color: "#555",
    whiteSpace: "nowrap",
    cursor: "pointer",
    "& input[type='checkbox']": {
      width: "auto",
      margin: 0,
      cursor: "pointer",
    },
  },
  fieldWrap: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    flex: 1,
    minWidth: 0,
    "&.is-hidden-field": {
      opacity: 0.55,
    },
  },
  label: {
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    color: "#374151",
    fontFamily: "objektiv-mk2, sans-serif",
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
  },
  input: {
    width: "50%",
    flex: "0 0 50%",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    fontSize: "12px",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "left",
    height: "30px",
    padding: "4px 8px",
    boxSizing: "border-box",
    "&:focus": {
      outline: "none",
      borderColor: "#4A7396",
    },
    "&:disabled": {
      backgroundColor: "#f3f4f6",
      color: "#9ca3af",
      cursor: "not-allowed",
    },
  },
  inputError: {
    borderColor: "#c62828",
    backgroundColor: "#fef2f2",
  },
  revertButton: {
    flex: "0 0 auto",
    border: "1px solid #4A7396",
    background: "#fff",
    color: "#4A7396",
    borderRadius: "6px",
    fontSize: "11px",
    fontWeight: 700,
    fontFamily: "objektiv-mk2, sans-serif",
    textTransform: "uppercase",
    padding: "6px 10px",
    height: "30px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
  revertButtonUnchanged: {
    borderColor: "#c4c4c4",
    background: "#f3f4f6",
    color: "#9ca3af",
    cursor: "pointer",
  },
  counter: {
    fontSize: "10px",
    color: "#6b7280",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "left",
  },
  counterError: {
    color: "#c62828",
    fontWeight: 700,
  },
  hiddenHint: {
    fontSize: "10px",
    color: "#9a3412",
    fontFamily: "objektiv-mk2, sans-serif",
    fontWeight: 600,
    lineHeight: 1.3,
  },
  responsibilityNote: {
    marginTop: 4,
    fontSize: "12px",
    color: "#c62828",
    textAlign: "center",
    fontFamily: "objektiv-mk2, sans-serif",
  },
  dialogFooter: {
    padding: "16px 24px",
    background: "#f9fafb",
    borderTop: "1px solid #e5e7eb",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
    alignItems: "center",
  },
  footerLeft: {
    justifySelf: "start",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  footerCenter: {
    justifySelf: "center",
  },
  footerRight: {
    justifySelf: "end",
  },
  saveError: {
    margin: "0 24px 12px",
    fontSize: "12px",
    color: "#c62828",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
  },
});

const DashboardBodyRangeEditorModal = ({
  open,
  rangeNum,
  editFormData,
  isDirty = false,
  onClose,
  onChange,
  onVisibilityChange,
  onRevertField,
  onRevertAllFields,
  onSave,
  saving = false,
  saveSuccess = false,
  saveError = null,
}) => {
  const classes = useStyles();
  const isLocked = saving || saveSuccess;

  if (!editFormData?.displayOverrides) {
    return null;
  }

  const fields = getRangeDisplayFieldDefs().map((field) =>
    field.name === "priceRange" && rangeNum != null
      ? { ...field, label: `Price Range #${rangeNum}` }
      : field,
  );
  const leftFields = fields.slice(0, 5);
  const rightFields = fields.slice(5);
  const { displayOverrides, fieldVisibility, originalValues } = editFormData;
  const hasOverLimitField = fields.some(({ name }) => {
    const value =
      displayOverrides[name] != null ? String(displayOverrides[name]) : "";
    return value.length > MAX_RANGE_FIELD_LENGTH;
  });
  const allMatchOriginal = fields.every(({ name }) => {
    if (!(name in (originalValues || {}))) {
      return false;
    }
    const value =
      displayOverrides[name] != null ? String(displayOverrides[name]) : "";
    const originalValue = String(
      originalValues[name] != null ? originalValues[name] : "",
    );
    return value === originalValue;
  });
  const canRevertAny = fields.some(
    ({ name }) => name in (originalValues || {}),
  );

  const saveLabel = saveSuccess
    ? "Saved"
    : saving
      ? "Saving..."
      : "Save Changes";

  const renderField = ({ name, label }) => {
    const canToggleDisplay = name !== "priceRange";
    const isVisible = canToggleDisplay
      ? fieldVisibility?.[name] !== false
      : true;
    const value =
      displayOverrides[name] != null ? String(displayOverrides[name]) : "";
    const originalValue =
      originalValues && name in originalValues
        ? String(originalValues[name] != null ? originalValues[name] : "")
        : null;
    const isSameAsOriginal = originalValue != null && value === originalValue;
    const charCount = value.length;
    const overLimit = charCount > MAX_RANGE_FIELD_LENGTH;
    const willHideOnDashboard = !isVisible || !hasDisplayValue(value);
    return (
      <div key={name} className={classes.fieldRow}>
        {canToggleDisplay ? (
          <label
            className={classes.visibility}
            htmlFor={`dashboard-range-visible-${name}`}
          >
            <input
              id={`dashboard-range-visible-${name}`}
              name={name}
              type="checkbox"
              checked={isVisible}
              onChange={onVisibilityChange}
              disabled={isLocked}
            />
            <span>Display</span>
          </label>
        ) : (
          <div style={{ width: 54, flex: "0 0 64px" }} aria-hidden="true" />
        )}
        <div
          className={`${classes.fieldWrap}${
            isVisible ? "" : " is-hidden-field"
          }`}
        >
          <label className={classes.label} htmlFor={`dashboard-range-${name}`}>
            {label}
          </label>
          <div className={classes.inputRow}>
            <input
              id={`dashboard-range-${name}`}
              className={`${classes.input}${
                overLimit ? ` ${classes.inputError}` : ""
              }`}
              name={name}
              type="text"
              value={value}
              maxLength={MAX_RANGE_FIELD_LENGTH}
              onChange={onChange}
              disabled={isLocked || !isVisible}
            />
            <button
              type="button"
              className={`${classes.revertButton}${
                isSameAsOriginal ? ` ${classes.revertButtonUnchanged}` : ""
              }`}
              onClick={() => onRevertField?.(name)}
              disabled={isLocked || !isVisible || originalValue == null}
              aria-pressed={!isSameAsOriginal}
              title={
                isSameAsOriginal
                  ? "Already matching original"
                  : "Revert to original value"
              }
            >
              Revert to Original
            </button>
          </div>
          <span
            className={`${classes.counter}${
              overLimit || charCount >= MAX_RANGE_FIELD_LENGTH
                ? ` ${classes.counterError}`
                : ""
            }`}
          >
            {charCount}/{MAX_RANGE_FIELD_LENGTH} Characters
          </span>
          {willHideOnDashboard ? (
            <span className={classes.hiddenHint}>
              {!isVisible
                ? "The text for this field is now hidden. ('DISPLAY' has been turned OFF)"
                : 'The text for this field is now hidden. (Field is empty or "0")'}
            </span>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <MailProDialog
      open={open}
      onClose={isLocked ? undefined : onClose}
      disableEscapeKeyDown={isLocked}
      classes={{ paper: classes.dialogPaper }}
    >
      <MailProDialogHeader>
        <div className={classes.dialogHeader}>
          <div className={classes.headerTitle}>
            Edit Price Range{rangeNum != null ? ` #${rangeNum}` : ""}
          </div>
        </div>
      </MailProDialogHeader>
      <MailProDialogBody>
        <div className={classes.dialogBody}>
          <div className={classes.instruction}>
            You have the option to EDIT any of the text or numbers as they
            appear on page 2. Uncheck 'DISPLAY' to hide a number or text.
          </div>

          <div className={classes.fields}>
            <div className={classes.fieldColumn}>
              {leftFields.map(renderField)}
            </div>
            <div className={classes.fieldColumn}>
              {rightFields.map(renderField)}
            </div>
          </div>

          <div className={classes.responsibilityNote}>
            PLEASE NOTE: You have made edits to the data or text fields. By
            making these changes you are assuming responsibility for what will
            print.
          </div>
        </div>
      </MailProDialogBody>
      {saveError ? (
        <p className={classes.saveError} role="alert">
          {saveError}
        </p>
      ) : null}
      <div className={classes.dialogFooter}>
        <div className={classes.footerLeft}>
          <MailProButton
            variant="filled"
            label="Cancel"
            disabled={isLocked}
            onClick={onClose}
            customStyle={{
              fontSize: "12px",
              padding: "6px 14px",
              textTransform: "uppercase",
              fontWeight: "bold",
              minWidth: "auto",
              borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
              minHeight: DIALOG_BUTTON_MIN_HEIGHT,
              height: DIALOG_BUTTON_MIN_HEIGHT,
              boxSizing: "border-box",
            }}
          />
        </div>
        <div className={classes.footerCenter}>
          <MailProButton
            variant="filled"
            label={saveLabel}
            disabled={saving || hasOverLimitField || (!isDirty && !saveSuccess)}
            onClick={onSave}
            customStyle={{
              fontSize: "12px",
              padding: "8px 18px",
              textTransform: "uppercase",
              fontWeight: "bold",
              minWidth: "auto",
              borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
              minHeight: DIALOG_BUTTON_MIN_HEIGHT,
              height: DIALOG_BUTTON_MIN_HEIGHT,
              boxSizing: "border-box",
              ...(saveSuccess
                ? {
                    backgroundColor: "#2e7d32",
                    color: "#fff",
                    borderColor: "#2e7d32",
                    cursor: "default",
                  }
                : {}),
            }}
          />
        </div>
        <div className={classes.footerRight}>
          <button
            type="button"
            className={`${classes.revertButton}${
              allMatchOriginal ? ` ${classes.revertButtonUnchanged}` : ""
            }`}
            onClick={() => onRevertAllFields?.()}
            disabled={isLocked || !canRevertAny}
            title={
              allMatchOriginal
                ? "All fields already match original"
                : "Revert all fields to original values"
            }
          >
            Revert All Fields
          </button>
        </div>
      </div>
    </MailProDialog>
  );
};

export default DashboardBodyRangeEditorModal;
